import { languageLabel as sharedLanguageLabel } from '@copilot/ui';

import { BOT_ERROR_CODES } from '../constants/botMeetings';
import { LANGUAGES } from '../constants/languages';
import { isUsageLimitError } from '../services/pricingApi';
import { getUserFriendlyError } from './errors';

// Formatting, validation and the bot name are shared with Desktop
// (@copilot/ui's autoAttend.ts). What stays here is Web-only: turning a
// bot-meetings API failure into customer copy.
export {
  autoAttendDisplayName,
  firstNameOf,
  formatDateTime,
  formatSchedule,
  toDateTimeLocalValue,
  validateScheduleTime,
} from '@copilot/ui';

export const UNAVAILABLE_MESSAGE =
  "Auto Attend isn't available right now. Please try again later.";
export const SERVICE_BUSY_MESSAGE = "Couldn't reach the Auto Attend service. Try again.";
export const NOT_FOUND_MESSAGE = 'This Auto Attend meeting no longer exists.';
export const VALIDATION_MESSAGE = 'Please check the form and try again.';

export function languageLabel(code) {
  return sharedLanguageLabel(code, LANGUAGES);
}

export function isUnavailableError(error) {
  return error?.code === BOT_ERROR_CODES.UNAVAILABLE;
}

// The bot moved on (or went) since the list was fetched.
export function isStaleBotError(error) {
  return (
    error?.code === BOT_ERROR_CODES.STATE_CONFLICT || error?.code === BOT_ERROR_CODES.NOT_FOUND
  );
}

// One place that turns a bot-meetings API failure into customer copy.
export function describeBotError(error, fallback = 'Something went wrong. Please try again.') {
  switch (error?.code) {
    case BOT_ERROR_CODES.UNAVAILABLE:
      return UNAVAILABLE_MESSAGE;
    case BOT_ERROR_CODES.SERVICE_BUSY:
      return SERVICE_BUSY_MESSAGE;
    case BOT_ERROR_CODES.NOT_FOUND:
      return NOT_FOUND_MESSAGE;
    // The backend's own wording is already customer-safe for these.
    case BOT_ERROR_CODES.INVALID:
    case BOT_ERROR_CODES.REFUSED:
    case BOT_ERROR_CODES.LIMIT_REACHED:
    case BOT_ERROR_CODES.STATE_CONFLICT:
      return error.message || fallback;
    default:
      break;
  }

  // A FastAPI/Pydantic 422 has no code; its message would be a JSON
  // payload of validation details, which is never shown.
  if (error?.status === 422) return VALIDATION_MESSAGE;

  return getUserFriendlyError(error, fallback);
}

// A failed bot-meetings request, described for the shared Auto Attend UI.
export function botErrorInfo(error, fallback) {
  const planLimit = isUsageLimitError(error);
  return {
    // A usage-limit message is the backend's own customer-safe text.
    message: planLimit ? error.message : describeBotError(error, fallback),
    planLimit,
    stale: isStaleBotError(error),
  };
}
